import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { useAccess } from '@/access/useAccess'
import type { SectionId } from '@/access/sections'
import { api } from '@/api/client'
import type { Balance } from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'
import { authorName } from '@/features/approvals/labels'
import { useModerationQueues } from '@/features/approvals/useModerationQueues'
import { useReviewAction } from '@/features/approvals/useReviewAction'
import { bookingRequestsQuery } from '@/features/bookings/api'
import { unitsQuery } from '@/features/publish/api'
import { PublishModal, type PublishKind } from '@/features/publish/PublishModal'
import { ago, formatBRL, monthName } from '@/lib/format'
import { SUBTITLES } from '@/shell/sectionCopy'
import {
  Avatar,
  Button,
  Card,
  EmptyRow,
  Eyebrow,
  PageHeader,
  QuickAction,
  Row,
  SectionTitle,
  Spinner,
  StatTile,
} from '@/ui'
import styles from './OverviewView.module.css'

const latestFirst = (a: Balance, b: Balance) =>
  b.reference_year - a.reference_year || b.reference_month - a.reference_month

export function OverviewView() {
  const { condoId, membership, sections } = useAccess()
  const navigate = useNavigate()
  const can = (id: SectionId) => sections.some((s) => s.id === id)
  const go = (id: SectionId) => {
    const section = sections.find((s) => s.id === id)
    if (section) navigate(`/c/${condoId}${section.path}`)
  }

  const { queue, reported, isLoading } = useModerationQueues(condoId, can('approvals'))
  const bookings = useQuery({
    ...bookingRequestsQuery(condoId, 'pending'),
    enabled: can('bookings'),
  })
  const units = useQuery({ ...unitsQuery(condoId), enabled: can('residents') })
  const balances = useQuery({
    queryKey: queryKeys.balances(condoId),
    queryFn: ({ signal }) =>
      api.get<Balance[]>('/condominiums/:id/balances', { params: { id: condoId }, signal }),
    enabled: can('balances'),
  })
  const review = useReviewAction(condoId)
  const [publish, setPublish] = useState<PublishKind | null>(null)

  const latest = [...(balances.data ?? [])].sort(latestFirst)[0]
  const waiting = queue.length + reported.length + (bookings.data?.length ?? 0)
  const occupied = units.data?.filter((u) => u.residents.length > 0).length ?? 0
  const towers = new Set(units.data?.map((u) => u.tower)).size

  const stats = [
    {
      id: 'approvals' as const,
      icon: 'flag' as const,
      label: 'Aprovações pendentes',
      value: queue.length,
      meta: 'Publicações e enquetes dos moradores',
    },
    {
      id: 'bookings' as const,
      icon: 'calendar_month' as const,
      label: 'Solicitações de reserva',
      value: bookings.data?.length ?? 0,
      meta: 'Aguardando confirmação',
    },
    {
      id: 'approvals' as const,
      icon: 'shield' as const,
      label: 'Conteúdo denunciado',
      value: reported.length,
      meta: 'Sinalizado pelos vizinhos',
    },
    {
      id: 'residents' as const,
      icon: 'group' as const,
      label: 'Unidades ocupadas',
      value: `${occupied}/${units.data?.length ?? 0}`,
      meta: `${towers} ${towers === 1 ? 'torre' : 'torres'}`,
    },
  ].filter((s) => can(s.id))

  const shortcuts = [
    can('overview') && {
      key: 'publish',
      icon: 'campaign' as const,
      label: 'Publicar um comunicado',
      meta: 'Vai ao ar sem revisão',
      onClick: () => setPublish('post'),
    },
    can('residents') && {
      key: 'residents',
      icon: 'group' as const,
      label: 'Adicionar morador a uma unidade',
      meta: 'Busque por e-mail ou telefone',
      onClick: () => go('residents'),
    },
    can('balances') && {
      key: 'balances',
      icon: 'upload' as const,
      label: 'Enviar balancete mensal',
      meta: latest
        ? `${monthName(latest.reference_month)} ${latest.reference_year} é o mais recente`
        : 'Publique o resultado do mês',
      onClick: () => go('balances'),
    },
    can('condo') && {
      key: 'condo',
      icon: 'apartment' as const,
      label: 'Editar dados do condomínio',
      meta: 'Regras, contatos, história',
      onClick: () => go('condo'),
    },
  ].filter((a) => a !== false)

  return (
    <>
      <PageHeader
        title="Visão geral"
        subtitle={SUBTITLES.overview({ condoName: membership.condominium_name, waiting })}
        actions={
          <>
            <Button variant="secondary" icon="campaign" onClick={() => setPublish('post')}>
              Publicar comunicado
            </Button>
            <Button icon="poll" onClick={() => setPublish('poll')}>
              Abrir enquete
            </Button>
          </>
        }
      />

      {stats.length > 0 && (
        <div className={styles.stats}>
          {stats.map((s) => (
            <StatTile
              key={s.label}
              icon={s.icon}
              label={s.label}
              value={s.value}
              meta={s.meta}
              onClick={() => go(s.id)}
            />
          ))}
        </div>
      )}

      <div className={can('approvals') ? styles.columns : styles.single}>
        {can('approvals') && (
          <section aria-label="Precisa da sua atenção">
            <SectionTitle
              action={
                <Button variant="ghost" size="s" onClick={() => go('approvals')}>
                  Ver tudo
                </Button>
              }
            >
              Precisa da sua atenção
            </SectionTitle>
            <Card pad={0}>
              {isLoading ? (
                <div className={styles.loading}>
                  <Spinner size={28} />
                </div>
              ) : queue.length === 0 ? (
                <EmptyRow
                  icon="check"
                  title="Fila vazia"
                  description="Nenhuma publicação ou enquete aguardando revisão."
                />
              ) : (
                queue.slice(0, 4).map((item) => {
                  const name = authorName(item.author)
                  return (
                    <Row key={`${item.kind}-${item.id}`} className={styles.item}>
                      <Avatar name={name} size={36} />
                      <div className={styles.text}>
                        <div className={styles.name}>{name}</div>
                        <div className={styles.meta}>
                          {item.unit.number} · {item.unit.tower.name} · {ago(item.created_at)}
                        </div>
                        <div className={styles.title}>{item.title}</div>
                        <div className={styles.body}>{item.body}</div>
                      </div>
                      <div className={styles.actions}>
                        <Button
                          variant="secondary"
                          size="s"
                          aria-label={`Recusar ${item.title}`}
                          onClick={() => review.mutate({ action: 'reject', item })}
                        >
                          Recusar
                        </Button>
                        <Button
                          size="s"
                          aria-label={`Aprovar ${item.title}`}
                          onClick={() => review.mutate({ action: 'approve', item })}
                        >
                          Aprovar
                        </Button>
                      </div>
                    </Row>
                  )
                })
              )}
            </Card>
          </section>
        )}

        <div className={styles.side}>
          {shortcuts.length > 0 && (
            <section aria-label="Atalhos">
              <SectionTitle>Atalhos</SectionTitle>
              <div className={styles.shortcuts}>
                {shortcuts.map(({ key, ...a }) => (
                  <QuickAction key={key} {...a} />
                ))}
              </div>
            </section>
          )}
          {latest && (
            <Card aria-label="Último balancete">
              <Eyebrow>Último balancete</Eyebrow>
              <div className={styles.balanceTitle}>
                {monthName(latest.reference_month)} {latest.reference_year}
              </div>
              <div className={styles.balanceRows}>
                <BalanceRow label="Receitas" value={latest.total_income} tone="income" />
                <BalanceRow label="Despesas" value={latest.total_expenses} tone="expense" />
                <BalanceRow
                  label="Resultado"
                  value={latest.total_income - latest.total_expenses}
                  tone="result"
                />
              </div>
            </Card>
          )}
        </div>
      </div>

      {publish && <PublishModal kind={publish} onClose={() => setPublish(null)} />}
    </>
  )
}

function BalanceRow({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'income' | 'expense' | 'result'
}) {
  return (
    <div className={styles.balanceRow}>
      <span className={styles.balanceLabel}>{label}</span>
      <span className={`${styles.balanceValue} ${styles[tone]}`}>{formatBRL(value)}</span>
    </div>
  )
}
